import React from 'react';
import { useConsent } from 'react-hook-consent';

// Labels in multiple languages to find the Settings button
const LABELS = ['Settings', 'Einstellungen', 'Ρυθμίσεις', 'ตั้งค่า'];

const norm = s =>
  (s || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const isVisible = el => {
  if (!el) return false;
  const s = window.getComputedStyle(el);
  if (s.display === 'none' || s.visibility === 'hidden' || parseFloat(s.opacity) === 0)
    return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
};

const closestClickable = el => el?.closest?.('button, a, [role="button"], [onclick]') || el;

const findSettingsEl = () => {
  const labels = LABELS.map(norm);
  const matches = v => labels.some(l => norm(v).includes(l));

  // First, look specifically within the consent banner container
  const consentContainers = document.querySelectorAll(
    '[class*="consent"], [class*="cookie"], [class*="banner"], [data-consent], [data-cookie]'
  );
  
  for (const container of consentContainers) {
    const buttons = container.querySelectorAll('button, a, [role="button"]');
    for (const el of buttons) {
      const txt = el.textContent || el.innerText || '';
      const aria = el.getAttribute?.('aria-label') || '';
      if ((txt && matches(txt)) || (aria && matches(aria))) {
        console.log('[ConsentOpenBridge] Found Settings in consent container:', el);
        return closestClickable(el);
      }
    }
  }

  // Fallback: Look for buttons with Settings text that are likely consent-related
  const allButtons = document.querySelectorAll('button, [role="button"]');
  for (const el of allButtons) {
    const txt = (el.textContent || el.innerText || '').trim();
    // Be more specific - look for exact or near-exact matches
    if (labels.some(l => norm(txt) === l || norm(txt) === l + ' cookies')) {
      console.log('[ConsentOpenBridge] Found Settings button:', el);
      return closestClickable(el);
    }
  }

  // Heuristic data-attrs
  const dataAttrEl = document.querySelector(
    '[data-consent-settings], [data-cookie-settings], [data-rhc-settings]'
  );
  if (dataAttrEl) {
    console.log('[ConsentOpenBridge] Found Settings via data attr:', dataAttrEl);
    return dataAttrEl;
  }

  return null;
};

const waitFor = async (fn, { timeout = 4000, interval = 120 } = {}) => {
  const start = Date.now();
  while (true) {
    const result = fn();
    if (result) return result;
    if (Date.now() - start >= timeout) return null;
    await new Promise(r => setTimeout(r, interval));
  }
};

/**
 * Helper to get stored consent from localStorage
 */
const getStoredConsent = () => {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem('react-hook-consent');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('[ConsentOpenBridge] Failed to read stored consent:', e);
  }
  return null;
};

/**
 * Check if user has made any consent decision
 */
const hasUserConsented = () => {
  const stored = getStoredConsent();
  return stored !== null;
};

const ConsentOpenBridge = () => {
  const ctx = (typeof useConsent === 'function' ? useConsent() : {}) || {};
  const { consent, toggleBanner, isBannerOpen, toggleDetails, isDetailsVisible } = ctx;

  React.useEffect(() => {
    // Expose helper to check current consent state
    window.__getConsentState = () => ({
      consent: consent || [],
      hasConsented: hasUserConsented(),
      storedConsent: getStoredConsent(),
      isBannerOpen,
      isDetailsVisible,
      availableMethods: Object.keys(ctx).filter(k => typeof ctx[k] === 'function'),
    });

    /**
     * ✅ Main function to open consent settings
     * Does NOT reset consent - preserves user's existing choices
     */
    window.__openConsent = async () => {
      console.log('[ConsentOpenBridge] Opening consent modal...');
      console.log('[ConsentOpenBridge] Available context methods:', 
        Object.keys(ctx).filter(k => typeof ctx[k] === 'function')
      );
      console.log('[ConsentOpenBridge] Context state:', {
        isBannerOpen,
        isDetailsVisible,
        consent,
      });

      // 1) Try toggleDetails if available (this might open the modal directly)
      if (typeof toggleDetails === 'function') {
        console.log('[ConsentOpenBridge] Using toggleDetails()');
        toggleDetails();
        return;
      }

      // 2) Try official API methods if present
      if (typeof ctx.openModal === 'function') {
        console.log('[ConsentOpenBridge] Using ctx.openModal()');
        ctx.openModal();
        return;
      }

      if (typeof ctx.setIsModalOpen === 'function') {
        console.log('[ConsentOpenBridge] Using ctx.setIsModalOpen(true)');
        ctx.setIsModalOpen(true);
        return;
      }

      // 3) Ensure banner is visible first
      if (typeof toggleBanner === 'function' && !isBannerOpen) {
        console.log('[ConsentOpenBridge] Banner not open, toggling...');
        toggleBanner();
        // Wait for banner to render
        await new Promise(r => setTimeout(r, 300));
      }

      // 4) Now find and click the Settings button
      let settingsEl = await waitFor(() => {
        const el = findSettingsEl();
        if (el && isVisible(el)) {
          return el;
        }
        return null;
      }, { timeout: 2000, interval: 100 });

      if (settingsEl) {
        console.log('[ConsentOpenBridge] Found Settings button:', settingsEl);
        console.log('[ConsentOpenBridge] Button HTML:', settingsEl.outerHTML);
        
        // Try to find React's internal onClick handler and call it
        const reactKey = Object.keys(settingsEl).find(k => k.startsWith('__reactFiber') || k.startsWith('__reactInternalInstance'));
        if (reactKey) {
          console.log('[ConsentOpenBridge] Found React fiber key:', reactKey);
        }

        // Try multiple approaches
        console.log('[ConsentOpenBridge] Attempting click...');
        
        // Approach 1: Direct click
        settingsEl.click();
        
        await new Promise(r => setTimeout(r, 150));
        
        // Check if it worked
        const modalNow = document.querySelector('[class*="modal"], [role="dialog"]');
        if (modalNow && isVisible(modalNow)) {
          console.log('[ConsentOpenBridge] Modal opened successfully');
          return;
        }

        // Approach 2: Dispatch MouseEvent with all properties
        console.log('[ConsentOpenBridge] Direct click may have failed, trying MouseEvent...');
        const clickEvent = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true,
          buttons: 1,
        });
        settingsEl.dispatchEvent(clickEvent);
        
        await new Promise(r => setTimeout(r, 150));
        
        // Approach 3: Try triggering via pointer events
        console.log('[ConsentOpenBridge] Trying pointer events...');
        settingsEl.dispatchEvent(new PointerEvent('pointerdown', { bubbles: true }));
        settingsEl.dispatchEvent(new PointerEvent('pointerup', { bubbles: true }));
        
        return;
      }

      console.warn('[ConsentOpenBridge] Could not find Settings button');
      
      // Last resort: just toggle the banner
      if (typeof toggleBanner === 'function') {
        console.log('[ConsentOpenBridge] Falling back to toggleBanner');
        toggleBanner();
      }
    };

    return () => {
      try {
        delete window.__openConsent;
        delete window.__getConsentState;
      } catch {
        window.__openConsent = undefined;
        window.__getConsentState = undefined;
      }
    };
  }, [ctx, consent, toggleBanner, isBannerOpen, toggleDetails, isDetailsVisible]);

  return null;
};

export default ConsentOpenBridge;